import { supabase } from "@/integrations/supabase/client";

export interface MissingProductInfo {
  product_id: string;
  product_name: string;
  required: number;
  available_at_location: number;
  unit: string | null;
  available_locations: string[];
}

export interface TreatmentLocationValidationResult {
  valid: boolean;
  missingProducts: MissingProductInfo[];
}

interface ProductRequirement {
  product_id: string;
  required: number;
}

/**
 * Validates whether a treatment can be executed at a given location.
 *
 * Authoritative rule (matches backend):
 *   - Resolve effective location from the task's Block -> Nave -> Localización.
 *   - For each treatment product, check ONLY that location.
 *   - Legacy products with NO inventory_location_stock rows at all are global
 *     and considered valid (existing behavior).
 *   - A product is "missing" only when it has at least one location_stock row
 *     somewhere AND the available quantity at the effective location is strictly
 *     less than the required amount for the task.
 *
 * Pass `productRequirements` whenever you know the exact required amount per
 * product (per-ha already resolved). When omitted, the helper falls back to
 * "any positive stock at this location passes", which preserves prior behaviour
 * for call sites that only want a soft pre-check.
 */
export async function validateTreatmentLocationStock(
  companyId: string,
  treatmentId: string,
  effectiveLocationId: string,
  productRequirements?: ProductRequirement[]
): Promise<TreatmentLocationValidationResult> {
  // 1. Get treatment components with product names + units
  const { data: components, error: compError } = await supabase
    .from("treatment_components")
    .select("product_id, unit, inventory!inner(product_name, unit)")
    .eq("treatment_id", treatmentId)
    .eq("company_id", companyId);

  if (compError || !components || components.length === 0) {
    return { valid: true, missingProducts: [] };
  }

  const productIds = components.map((c: any) => c.product_id);

  // 2. Get all location_stock rows for these products in this company
  const { data: allStockRows } = await supabase
    .from("inventory_location_stock")
    .select("product_id, location_id, quantity")
    .eq("company_id", companyId)
    .in("product_id", productIds);

  // Map: product_id -> array of { location_id, quantity }
  const stockByProduct = new Map<string, Array<{ location_id: string; quantity: number }>>();
  for (const row of allStockRows || []) {
    const arr = stockByProduct.get(row.product_id) || [];
    arr.push({ location_id: row.location_id, quantity: Number(row.quantity) || 0 });
    stockByProduct.set(row.product_id, arr);
  }

  const reqMap = new Map<string, number>();
  for (const r of productRequirements || []) {
    reqMap.set(r.product_id, r.required);
  }

  const missing: Array<{
    product_id: string;
    product_name: string;
    unit: string | null;
    required: number;
    available_at_location: number;
    other_locations: Array<{ location_id: string; quantity: number }>;
  }> = [];

  for (const c of components as any[]) {
    const pid = c.product_id;
    const productName = c.inventory?.product_name || "?";
    const unit = c.unit || c.inventory?.unit || null;
    const required = reqMap.get(pid) ?? 0;

    const rows = stockByProduct.get(pid);

    // Legacy/global product: no per-location rows anywhere -> always valid
    if (!rows || rows.length === 0) continue;

    // Per-location product: check effective location only
    const here = rows.find((r) => r.location_id === effectiveLocationId);
    const availableHere = here ? here.quantity : 0;

    // Determine if this product is missing for this task:
    //  - If we know the required amount: fail when availableHere < required
    //  - If we don't know required (soft pre-check): fail only when availableHere <= 0
    const failsCheck = productRequirements
      ? availableHere < required
      : availableHere <= 0;

    if (!failsCheck) continue;

    const others = rows.filter((r) => r.location_id !== effectiveLocationId && r.quantity > 0);
    missing.push({
      product_id: pid,
      product_name: productName,
      unit,
      required,
      available_at_location: availableHere,
      other_locations: others,
    });
  }

  if (missing.length === 0) {
    return { valid: true, missingProducts: [] };
  }

  // Resolve names for the alternative locations
  const altLocIds = [
    ...new Set(missing.flatMap((m) => m.other_locations.map((o) => o.location_id))),
  ];
  let locsMap: Record<string, string> = {};
  if (altLocIds.length > 0) {
    const { data: locs } = await supabase
      .from("locations")
      .select("id, name")
      .in("id", altLocIds);
    locsMap = Object.fromEntries((locs || []).map((l) => [l.id, l.name]));
  }

  const missingProducts: MissingProductInfo[] = missing.map((m) => ({
    product_id: m.product_id,
    product_name: m.product_name,
    required: m.required,
    available_at_location: m.available_at_location,
    unit: m.unit,
    available_locations: m.other_locations
      .map((o) => locsMap[o.location_id])
      .filter(Boolean),
  }));

  return { valid: false, missingProducts };
}

/**
 * Resolve the effective Localización for a task's structure.
 * Block wins over greenhouse because Bloque is the most specific operational unit.
 */
export async function resolveEffectiveLocationId(input: {
  block_id?: string | null;
  block_ids?: string[] | null;
  greenhouse_id?: string | null;
}): Promise<string | null> {
  const blockId = input.block_id || (input.block_ids && input.block_ids[0]) || null;

  if (blockId) {
    const { data: block } = await supabase
      .from("blocks")
      .select("greenhouse_id")
      .eq("id", blockId)
      .is("archived_at", null)
      .maybeSingle();
    if (block?.greenhouse_id) {
      const { data: gh } = await supabase
        .from("greenhouses")
        .select("location_id")
        .eq("id", block.greenhouse_id)
        .maybeSingle();
      if (gh?.location_id) return gh.location_id;
    }
  }

  if (input.greenhouse_id) {
    const { data: gh } = await supabase
      .from("greenhouses")
      .select("location_id")
      .eq("id", input.greenhouse_id)
      .maybeSingle();
    if (gh?.location_id) return gh.location_id;
  }

  return null;
}
